/*
 * NEXZ V2 — cart drawer set suggestion (Stage H, restored as one compact
 * row; markup and the reasons for it in snippets/nexz-cart-upsell.liquid)
 *
 * Unlike the PDP's Complete the Set (nexz-complete-set.js), one side of
 * the pair is already in the cart by the time this renders — this only
 * ever adds the ONE partner variant, so it reuses the same single-item
 * add path product-form.js itself uses (Theme.routes.cart_add_url +
 * fetchConfig, both already established in Stage F), not the two-line
 * JSON body that snippet needs.
 *
 * Session dismissal: sessionStorage, not localStorage — doc 10 "never
 * re-nags in session," not "never again." A fresh session (new tab,
 * next day) sees it again if the cart still qualifies.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

class NexzCartUpsell extends HTMLElement {
  connectedCallback() {
    const dismissKey = this.dataset.dismissKey;
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(dismissKey) === 'true';
    } catch (e) {
      // sessionStorage unavailable (private browsing) — show it; no
      // harm in a merchant seeing the upsell twice, worse to hide it
      // always.
    }
    if (dismissed) {
      this.hidden = true;
      return;
    }

    const dismissButton = this.querySelector('[data-nexz-upsell-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        this.#fadeOut();
        try {
          sessionStorage.setItem(dismissKey, 'true');
        } catch (e) {
          // Same as above — non-fatal.
        }
      });
    }

    const addButton = this.querySelector('[data-nexz-upsell-add]');
    if (addButton) {
      addButton.addEventListener('click', () => this.#handleAdd(addButton, dismissKey));
    }
  }

  /**
   * Fades the row out before removing it, so the drawer content below
   * doesn't jump the instant × is pressed. Opacity only (no layout), and
   * no fade under reduced motion.
   */
  #fadeOut() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof this.animate !== 'function') {
      this.hidden = true;
      return;
    }
    const styles = getComputedStyle(document.documentElement);
    const easing = styles.getPropertyValue('--nexz-ease-out').trim() || 'ease-out';
    // The token may arrive as "150ms" or, after Shopify minifies the CSS,
    // ".15s"; WAAPI wants milliseconds.
    const token = styles.getPropertyValue('--nexz-motion-fast').trim();
    const duration = (token.endsWith('ms') ? parseFloat(token) : parseFloat(token) * 1000) || 150;
    const hide = () => {
      this.hidden = true;
    };
    this.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing }).finished.then(hide, hide);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} dismissKey
   */
  #handleAdd(button, dismissKey) {
    const sizeSelect = this.querySelector('[data-nexz-upsell-size]');
    const variantId = sizeSelect ? sizeSelect.value : this.dataset.partnerProductId;
    const errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-nexz-upsell-error]'));
    const errorText = this.dataset.errorText || 'Could not add to cart.';
    // The size select starts on an empty "Select a size" option: nothing is
    // added until the shopper picks a size.
    if (!variantId) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = this.dataset.sizeText || errorText;
      }
      if (sizeSelect instanceof HTMLSelectElement) {
        sizeSelect.setAttribute('aria-invalid', 'true');
        sizeSelect.focus();
        sizeSelect.addEventListener('change', () => sizeSelect.removeAttribute('aria-invalid'), { once: true });
      }
      return;
    }

    button.disabled = true;
    if (errorEl) errorEl.hidden = true;

    // Same event contract as product-form.js: listeners (header count,
    // drawer, cart-items) read a full cart from the resolved promise, and
    // the drawer morphs from the sections rendered with the add. The
    // /cart/add.js body is only the added line, so the cart is refetched.
    const deferred = CartLinesUpdateEvent.createPromise();
    // Handled here as well as by cart-drawer.js — see nexz-cart-undo.js for
    // the rationale. The inline error element below stays the user-facing
    // report; this only stops an unhandled rejection reaching monitoring.
    deferred.promise.catch(() => {});

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'set',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const message = response.description || response.message || errorText;
          deferred.reject(new Error(message));
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = message;
          }
          return;
        }
        try {
          sessionStorage.setItem(dismissKey, 'true');
        } catch (e) {
          // Non-fatal.
        }
        this.hidden = true;
        const ajaxCart = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            itemCount: 1,
            source: 'nexz-cart-upsell',
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = errorText;
        }
      })
      .finally(() => {
        button.disabled = false;
      });
  }
}

if (!customElements.get('nexz-cart-upsell')) {
  customElements.define('nexz-cart-upsell', NexzCartUpsell);
}
